// ═══════════════════════════════════════════════════════════════
// SHOW FORM MODAL
// Path: src/components/shows/ShowFormModal.jsx
//
// Add/Edit a show (production). Used by Shows tab in ShowsView.
//
// Props:
//   show      — existing show object (null = create mode)
//   onSave    — async (showData) => void
//   onClose   — () => void
//
// Blueprint: CMM_Show_Sponsorship_Blueprint_v1_1.md — Phase 4
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { Theater } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY } from "../../constants/appConfig";
import { Modal } from "../ui/Modal";
import { FormInput, FormSelect } from "../ui/FormControls";
import { ActionButton } from "../ui/ActionButton";

const STATUS_OPTIONS = [
  { value: "upcoming", label: "Upcoming" },
  { value: "active", label: "Active" },
  { value: "closed", label: "Closed" },
  { value: "archived", label: "Archived" },
];

const VENUE_OPTIONS = [
  { value: "", label: "— Select venue —" },
  { value: "Solvang", label: "Solvang" },
  { value: "Santa Maria", label: "Santa Maria" },
  { value: "Both", label: "Both" },
  { value: "Other", label: "Other" },
];

export const ShowFormModal = ({ show, onSave, onClose }) => {
  const { palette } = useApp();
  const isEdit = !!show;

  const [title, setTitle] = useState(show?.title || "");
  const [season, setSeason] = useState(show?.season || "");
  const [venue, setVenue] = useState(show?.venue || "");
  const [openDate, setOpenDate] = useState(show?.open_date || "");
  const [closeDate, setCloseDate] = useState(show?.close_date || "");
  const [status, setStatus] = useState(show?.status || "upcoming");
  const [description, setDescription] = useState(show?.description || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!season.trim()) {
      setError("Season is required.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        title: title.trim(),
        season: season.trim(),
        venue: venue || null,
        openDate: openDate || null,
        closeDate: closeDate || null,
        status,
        description: description.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save show.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit Show" : "Add Show"}
      icon={Theater}
      onClose={onClose}
      width={520}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <FormInput
          label="Title"
          value={title}
          onChange={(val) => { setTitle(val); setError(null); }}
          placeholder="e.g. Frozen Jr."
          required
        />

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <FormInput
              label="Season"
              value={season}
              onChange={(val) => { setSeason(val); setError(null); }}
              placeholder="e.g. 25/26"
              required
            />
          </div>
          <div style={{ flex: 1 }}>
            <FormSelect
              label="Venue"
              value={venue}
              onChange={setVenue}
              options={VENUE_OPTIONS}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <FormInput
              label="Opening Date"
              value={openDate}
              onChange={setOpenDate}
              type="date"
            />
          </div>
          <div style={{ flex: 1 }}>
            <FormInput
              label="Closing Date"
              value={closeDate}
              onChange={setCloseDate}
              type="date"
            />
          </div>
        </div>

        <FormSelect
          label="Status"
          value={status}
          onChange={setStatus}
          options={STATUS_OPTIONS}
        />

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{
            fontSize: FONT_SIZES.sm, fontWeight: 600,
            color: palette.text2, fontFamily: FONT_FAMILY,
          }}>
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Internal notes about this production"
            rows={3}
            style={{
              padding: "8px 12px", borderRadius: 8,
              fontSize: FONT_SIZES.md, fontFamily: FONT_FAMILY,
              outline: "none", background: palette.inputBg,
              color: palette.inputColor,
              border: `1px solid ${palette.inputBorder}`,
              resize: "vertical",
            }}
          />
        </div>

        {error && (
          <div style={{
            padding: "8px 12px", borderRadius: 8,
            background: "#fef2f2", border: "1px solid #fecaca",
            color: "#dc2626", fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 8 }}>
          <ActionButton label="Cancel" variant="secondary" onClick={onClose} />
          <ActionButton
            label={saving ? "Saving..." : isEdit ? "Save Changes" : "Add Show"}
            onClick={handleSave}
            disabled={saving || !title.trim() || !season.trim()}
          />
        </div>
      </div>
    </Modal>
  );
};
